<script setup lang="ts">
import type { JobModel } from '@/types/jobModel';
import JobListItem from '@/views/career/JobListItem.vue';

defineOptions({
  name: 'CompanyTopJobs',
});

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const { fetchAllJobsSorted, jobListRef } = useMopJob();
const topJobsRef: Ref<JobModel[]> = ref([]);
const MAX_ITEMS_LENGTH = 4;

onMounted(async () => {
  await fetchAllJobsSorted();

  if (jobListRef.value.length === 0) {
    return;
  }

  const vacanciesIds = props?.data?.items.map((job: { jobId: any }) => job.jobId);

  const topJobs = jobListRef.value.filter((jobRef) => vacanciesIds.includes(jobRef.getId()));
  const jobsNumberDifference = MAX_ITEMS_LENGTH - topJobs.length;
  const fallbackJobs = [];

  if (jobsNumberDifference > 0) {
    const latestJobs = jobListRef.value.filter((item) => !vacanciesIds.includes(item.getId()));

    for (let index = 0; index < jobsNumberDifference && index < latestJobs.length; index++) {
      fallbackJobs.push(latestJobs[index]);
    }
  }
  topJobsRef.value = [...topJobs, ...fallbackJobs];
});
</script>

<template>
  <div v-if="topJobsRef.length > 0" v-storyblok-editable="data" class="company-top-jobs">
    <div v-if="data?.title" class="company-top-jobs__title">{{ data.title }}</div>
    <div class="company-top-jobs__list">
      <JobListItem
        v-for="item in topJobsRef"
        :key="`top-jobs-${item.getId()}`"
        :job="item"
        class="company-top-jobs__item"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.company-top-jobs {
  display: flex;
  flex-direction: column;
  gap: 13px;
  margin: 0 13px;
}

.company-top-jobs__title {
  @include text-style(strong);
  font-size: 27px;
  line-height: 27px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.company-top-jobs__list {
  --job-item-gap: 16px;

  display: flex;
  flex-wrap: wrap;
  gap: var(--job-item-gap);
}

.company-top-jobs__item {
  width: calc((100% - var(--job-item-gap) * 3) / 4);
  border: 1px solid $black;

  @include v2-apply-upto('mobile') {
    width: 100%;
  }
}
</style>
